import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SKIP_JWT_INTERCEPTOR } from 'src/app/interceptor/auth-context';
import { TranslateService } from 'src/app/services/translate.service';
import { baseUrl } from 'src/environments/environment';

/**
 * Calls to the v2 API (`/v2/...` in LatestBackend), used only by the new-xxx pages.
 *
 * It skips the old JWT interceptor on purpose: v2 answers 401 for things like a wrong
 * password, which the old interceptor would treat as "session expired" and log out.
 * v2 has its own tokens (stored under the V2 keys below) and refreshes them itself.
 */

/** A translation key plus its values, e.g. { key: 'v2.validation.too_short', params: { min: 6 } }. */
export interface V2MessageRef {
  key: string;
  params?: Record<string, unknown>;
}

/**
 * A v2 error. `message` and `fields` are in the language of the request;
 * `params` and `fieldKeys` let the page translate them again after a language switch.
 */
export class V2Error extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields: Record<string, string> = {},
    readonly retryAfter?: number,
    readonly params: Record<string, unknown> = {},
    readonly fieldKeys: Record<string, V2MessageRef> = {},
  ) {
    super(message);
  }
}

export interface V2Session {
  tokenType: 'Bearer';
  accessToken: string;
  expiresIn: number;
  refreshToken: string;
  refreshExpiresIn: number;
}

export interface V2User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneCountry: string | null;
  phoneNumber: string | null;
}

/** What login, login/totp and register answer with. */
export type V2AuthResult =
  | {
      status: 'SIGNED_IN'; session: V2Session; firebase: { idToken: string; refreshToken: string }; user: V2User;
      /** Only right after an authenticator is set up: show once, then forget. */
      backupCodes?: string[];
      /** Only after signing in with a backup code. */
      backupCodesLeft?: number;
    }
  | { status: 'TOTP_REQUIRED'; challengeToken: string; expiresIn: number }
  | { status: 'TOTP_SETUP_REQUIRED'; challengeToken: string; expiresIn: number; secret: string; otpauthUrl: string; user?: V2User }
  | { status: 'ACCOUNT_CREATED'; user: V2User };

export interface V2OtpSent {
  method: 'sms' | 'email';
  destination: string;
  expiresIn: number;
  resendIn: number;
}

export interface V2RegisterInput {
  firstName: string;
  lastName: string;
  email: string;
  phoneCountry: string;
  phoneNumber: string;
  password: string;
  referralSource: number | string;
  referralCode?: string;
  receiveEmail?: boolean;
  becomePartner?: boolean;
  companyName?: string;
  sourceTag?: string;
  sourceCountry?: string;
  method: 'sms' | 'email';
  code: string;
}

export const V2_ACCESS_TOKEN_KEY = 'ALTOMATE_V2_ACCESS_TOKEN';
export const V2_REFRESH_TOKEN_KEY = 'ALTOMATE_V2_REFRESH_TOKEN';

/**
 * Ends the v2 session: forgets the v2 tokens here and revokes the refresh token on the
 * server (best effort, never throws). Called from every place the app signs out, so
 * signing out of the old pages also ends v2. A plain function, not part of the service,
 * so old code can call it without new dependencies.
 */
export function endV2Session(): void {
  try {
    const refreshToken = localStorage.getItem(V2_REFRESH_TOKEN_KEY);
    localStorage.removeItem(V2_ACCESS_TOKEN_KEY);
    localStorage.removeItem(V2_REFRESH_TOKEN_KEY);
    if (refreshToken) {
      fetch(`${baseUrl}/v2/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
        keepalive: true,
      }).catch(() => undefined);
    }
  } catch {
    // Signing out must never fail because of v2.
  }
}

/** Shown when the request never reached the server (translation key). */
export const NETWORK_ERROR_KEY = 'Unable to connect. Please check your internet connection and try again.';

@Injectable({ providedIn: 'root' })
export class V2ApiService {
  private readonly http = inject(HttpClient);
  private readonly translate = inject(TranslateService);
  private refreshing?: Promise<boolean>;

  // ---------- auth ----------
  login(body: { email: string; password: string }) {
    return this.post<V2AuthResult>('/auth/login', body);
  }

  /** Second sign-in step: the authenticator `code`, or a `backupCode`. */
  loginTotp(body: { challengeToken: string; code?: string; backupCode?: string }) {
    return this.post<V2AuthResult>('/auth/login/totp', body);
  }

  emailAvailability(email: string) {
    return this.request<{ available: boolean }>('GET', `/auth/email-availability?email=${encodeURIComponent(email)}`);
  }

  referral(code: string) {
    return this.request<{ valid: boolean }>('GET', `/referrals/${encodeURIComponent(code)}`);
  }

  sendRegisterOtp(body: { method: 'sms' | 'email'; email: string; phoneCountry: string; phoneNumber: string }) {
    return this.post<V2OtpSent>('/auth/register/otp', body);
  }

  register(body: V2RegisterInput) {
    return this.post<V2AuthResult>('/auth/register', body);
  }

  // ---------- password reset ----------
  forgotPassword(email: string) {
    return this.post<{ sent: true; resendIn: number }>('/auth/password/forgot', { email });
  }

  checkResetLink(token: string) {
    return this.request<{ valid: true; email: string }>('GET', `/auth/password/reset/${encodeURIComponent(token)}`);
  }

  resetPassword(body: { token: string; password: string }) {
    return this.post<{ reset: true }>('/auth/password/reset', body);
  }

  me() {
    return this.request<{ user: V2User }>('GET', '/auth/me', undefined, true);
  }

  logout(): void {
    endV2Session();
  }

  // ---------- session ----------
  storeSession(session: V2Session): void {
    localStorage.setItem(V2_ACCESS_TOKEN_KEY, session.accessToken);
    localStorage.setItem(V2_REFRESH_TOKEN_KEY, session.refreshToken);
  }

  clearSession(): void {
    localStorage.removeItem(V2_ACCESS_TOKEN_KEY);
    localStorage.removeItem(V2_REFRESH_TOKEN_KEY);
  }

  // ---------- plumbing ----------
  private post<T>(path: string, body: unknown) {
    return this.request<T>('POST', path, body);
  }

  /** Returns `data`, or throws V2Error. With `auth`, sends the v2 token and refreshes it once if it expired. */
  async request<T>(method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: string, body?: unknown, auth = false): Promise<T> {
    try {
      return await this.send<T>(method, path, body, auth);
    } catch (error) {
      if (auth && error instanceof V2Error && error.code === 'TOKEN_EXPIRED' && (await this.refresh())) {
        return this.send<T>(method, path, body, auth);
      }
      throw error;
    }
  }

  private async send<T>(method: string, path: string, body: unknown, auth: boolean): Promise<T> {
    let headers = new HttpHeaders({ 'Accept-Language': this.language() });
    if (auth && !localStorage.getItem('ALTOMATE_ACCESS_TOKEN_KEY')) {
      // Signed out on the old side (by a path that didn't end v2): end v2 too.
      endV2Session();
      throw new V2Error(401, 'UNAUTHENTICATED', '');
    }
    const token = auth ? localStorage.getItem(V2_ACCESS_TOKEN_KEY) : null;
    if (token) headers = headers.set('Authorization', `Bearer ${token}`);
    try {
      const response = await firstValueFrom(this.http.request<{ data: T }>(method, `${baseUrl}/v2${path}`, {
        body, headers, context: new HttpContext().set(SKIP_JWT_INTERCEPTOR, true),
      }));
      return response.data;
    } catch (error) {
      throw this.toV2Error(error);
    }
  }

  private refresh(): Promise<boolean> {
    // One refresh at a time; parallel calls wait for the same one.
    this.refreshing ??= (async () => {
      const refreshToken = localStorage.getItem(V2_REFRESH_TOKEN_KEY);
      if (!refreshToken) return false;
      try {
        const { session } = await this.send<{ session: V2Session }>('POST', '/auth/refresh', { refreshToken }, false);
        this.storeSession(session);
        return true;
      } catch {
        this.clearSession();
        return false;
      }
    })().finally(() => (this.refreshing = undefined));
    return this.refreshing;
  }

  private toV2Error(error: unknown): V2Error {
    if (error instanceof HttpErrorResponse) {
      const body = error.error?.error;
      if (body?.code) return new V2Error(error.status, body.code, body.message || '', body.fields || {}, body.retryAfter, body.params || {}, body.fieldKeys || {});
      if (error.status === 0) return new V2Error(0, 'NETWORK_ERROR', NETWORK_ERROR_KEY);
      return new V2Error(error.status, 'INTERNAL_ERROR', 'Something went wrong. Please try again.');
    }
    return new V2Error(0, 'INTERNAL_ERROR', 'Something went wrong. Please try again.');
  }

  private language(): string {
    return this.translate.getStoredLanguage() || this.translate.getCurrentLang() || 'en';
  }
}
